import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  CreditCard as CardIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Shield as SystemTokenIcon,
} from '@mui/icons-material';
import { sispService } from '../../../services';

interface SispToken {
  id: string;
  token: string;
  lastFourDigits: string;
  cardType: string;
  cardHolder: string;
  expireMonth: string;
  expireYear: string;
  activo: string;
  isSystemToken?: number;
  createdAt: string;
  lastUsedAt: string | null;
  clienteId: string;
  clienteNome: string;
  clienteEmail: string;
  clienteTelefone: string;
}

const SispTokensPage: React.FC = () => {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<SispToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('S');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedToken, setSelectedToken] = useState<SispToken | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sispService.getTokens({
        page: page + 1,
        limit: rowsPerPage,
        activo: statusFilter,
        search: searchQuery,
      });

      setTokens(response.data || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching tokens:', err);
      setError(err.message || t('configSispTokens.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, searchQuery, t]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const stats = {
    total: totalCount,
    active: tokens.filter(t => t.activo === 'S').length,
    inactive: tokens.filter(t => t.activo === 'N').length,
  };

  const handleOpenDelete = (token: SispToken) => {
    setSelectedToken(token);
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedToken) return;

    setDeleting(true);
    try {
      await sispService.deleteToken(selectedToken.id);
      setDeleteDialog(false);
      setSelectedToken(null);
      fetchTokens();
    } catch (err: any) {
      setError(err.message || t('configSispTokens.errorDeactivating'));
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleSystemToken = async (token: SispToken) => {
    const willBeSystem = !token.isSystemToken;
    const confirm = window.confirm(
      willBeSystem
        ? `Marcar o cartão ****${token.lastFourDigits} (${token.clienteNome}) como TOKEN DO SISTEMA?\n\nEste cartão será usado para pagar serviços quando o utilizador usa o saldo da Wallet Pagali.`
        : `Remover o flag de token do sistema do cartão ****${token.lastFourDigits}?`
    );
    if (!confirm) return;
    try {
      await sispService.setSystemToken(token.id, willBeSystem);
      fetchTokens();
    } catch (err: any) {
      setError(err.message || 'Erro ao actualizar token');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCardTypeColor = (cardType: string) => {
    switch (cardType?.toUpperCase()) {
      case 'VISA':
        return '#1A1F71';
      case 'MASTERCARD':
        return '#EB001B';
      case 'VINTI4':
        return '#00A651';
      default:
        return '#666';
    }
  };

  const maskToken = (token: string) => {
    if (!token) return '-';
    if (token.length <= 8) return token;
    return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
  };

  if (loading && tokens.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t('configSispTokens.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('configSispTokens.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTokens}
          disabled={loading}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('configSispTokens.totalTokens')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('configSispTokens.active')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inactive}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('configSispTokens.inactive')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('configSispTokens.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="S">{t('configSispTokens.active')}</MenuItem>
              <MenuItem value="N">{t('configSispTokens.inactive')}</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.client')}</TableCell>
                <TableCell>{t('configSispTokens.card')}</TableCell>
                <TableCell>{t('configSispTokens.token')}</TableCell>
                <TableCell>{t('configSispTokens.validity')}</TableCell>
                <TableCell>{t('configSispTokens.createdAt')}</TableCell>
                <TableCell>{t('configSispTokens.lastUsed')}</TableCell>
                <TableCell align="center">{t('common.status')}</TableCell>
                <TableCell align="center" width={80}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token) => (
                <TableRow key={token.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {token.clienteNome || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {token.clienteEmail || token.clienteTelefone || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={token.cardType || 'CARD'}
                        size="small"
                        sx={{
                          bgcolor: getCardTypeColor(token.cardType),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      <Typography variant="body2" fontFamily="monospace">
                        **** {token.lastFourDigits || '****'}
                      </Typography>
                    </Box>
                    {token.cardHolder && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {token.cardHolder}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={token.token}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ cursor: 'help' }}>
                        {maskToken(token.token)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {token.expireMonth && token.expireYear ? (
                      <Typography variant="body2">
                        {token.expireMonth}/{token.expireYear}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(token.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(token.lastUsedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={token.activo === 'S' ? t('common.active') : t('common.inactive')}
                        size="small"
                        color={token.activo === 'S' ? 'success' : 'default'}
                      />
                      {!!token.isSystemToken && (
                        <Chip label="Sistema" size="small" color="warning" icon={<SystemTokenIcon />} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {token.activo === 'S' && (
                      <>
                      <Tooltip title={token.isSystemToken ? 'Remover como Token do Sistema' : 'Marcar como Token do Sistema (Wallet)'}>
                        <IconButton
                          size="small"
                          color={token.isSystemToken ? 'warning' : 'default'}
                          onClick={() => handleToggleSystemToken(token)}
                        >
                          <SystemTokenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('configSispTokens.deactivateToken')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDelete(token)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {tokens.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('configSispTokens.noTokens')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {t('configSispTokens.deactivateToken')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            {t('configSispTokens.deactivateConfirm')}
          </Typography>
          {selectedToken && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('common.client')}:</strong> {selectedToken.clienteNome}
              </Typography>
              <Typography variant="body2">
                <strong>{t('configSispTokens.card')}:</strong> **** **** **** {selectedToken.lastFourDigits}
              </Typography>
              <Typography variant="body2">
                <strong>{t('common.type')}:</strong> {selectedToken.cardType}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('configSispTokens.deactivateWarning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? t('configSispTokens.deactivating') : t('configSispTokens.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SispTokensPage;
